/**
 * Leaflet map view for project geo items.
 * - Renders Points (clustered), LineStrings, Polygons from MongoDB
 * - Supports drawing new shapes (Point/Line/Polygon) via leaflet-draw toolbar
 * - Click feature → popup with name, description, properties, edit/delete
 * - Drag markers to relocate (Point only)
 * - Auto-fit bounds on data change
 *
 * NOTE: react-leaflet v5 + Leaflet 1.9 + leaflet.markercluster + leaflet-draw
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, useMap, Marker, Polyline, Polygon, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Trash2, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Default Leaflet icon fix (CRA bundling drops the default icon URLs) ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STATUS_TYPE_COLORS = {
  Point: '#2563eb',
  LineString: '#10b981',
  Polygon: '#f59e0b',
  MultiPoint: '#2563eb',
  MultiLineString: '#10b981',
  MultiPolygon: '#f59e0b',
};

// Convert GeoJSON coords (lon, lat) → Leaflet (lat, lon)
const swap2 = ([lon, lat]) => [lat, lon];
const swapLine = (coords) => coords.map(swap2);
const swapPolygon = (rings) => rings.map(swapLine);

// ==================== Cluster layer for Points ====================
function ClusterLayer({ pointItems, onItemClick }) {
  const map = useMap();
  const clusterRef = useRef(null);
  const itemsMapRef = useRef(new Map());

  useEffect(() => {
    const group = L.markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        let size = 'small';
        if (count >= 100) size = 'large';
        else if (count >= 10) size = 'medium';
        return L.divIcon({
          html: `<div class="cluster-${size}"><span>${count}</span></div>`,
          className: 'custom-marker-cluster',
          iconSize: L.point(40, 40),
        });
      },
    });
    clusterRef.current = group;
    map.addLayer(group);

    // Group-level click delegation — cluster group otherwise can swallow per-marker clicks.
    const onClusterClick = (e) => {
      const layer = e.layer;
      const itemId = layer?.options?.itemId;
      if (itemId) {
        const it = itemsMapRef.current.get(itemId);
        if (it) onItemClick(it);
      }
    };
    group.on('click', onClusterClick);

    return () => {
      group.off('click', onClusterClick);
      map.removeLayer(group);
      clusterRef.current = null;
    };
  }, [map, onItemClick]);

  useEffect(() => {
    const group = clusterRef.current;
    if (!group) return;
    group.clearLayers();
    const newMap = new Map();
    pointItems.forEach((it) => {
      newMap.set(it.id, it);
      const coords = it.geometry.coordinates;
      if (it.geometry.type === 'Point') {
        const marker = L.marker(swap2(coords), { itemId: it.id });
        marker.bindTooltip(it.name || '(adsız)', { direction: 'top' });
        group.addLayer(marker);
      } else if (it.geometry.type === 'MultiPoint') {
        coords.forEach((c) => {
          const m = L.marker(swap2(c), { itemId: it.id });
          m.bindTooltip(it.name || '(adsız)');
          group.addLayer(m);
        });
      }
    });
    itemsMapRef.current = newMap;
  }, [pointItems]);

  return null;
}

// ==================== Auto-fit bounds ====================
function FitBounds({ items, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (!items || items.length === 0) return;
    try {
      const layer = L.geoJSON({
        type: 'FeatureCollection',
        features: items.map((it) => ({
          type: 'Feature',
          geometry: it.geometry,
          properties: {},
        })),
      });
      const b = layer.getBounds();
      if (b.isValid()) {
        map.fitBounds(b, { padding: [40, 40], maxZoom: 16 });
      }
    } catch (e) {
      // ignore
    }
  }, [trigger, items, map]);
  return null;
}

// ==================== Drawing toolbar ====================
function DrawControl({ canEdit, onCreate, onEdit, onDelete }) {
  const map = useMap();
  const drawnRef = useRef(null);

  useEffect(() => {
    if (!canEdit) return;
    // Layer to hold *temporary* drawings until persisted to server.
    const drawnItems = new L.FeatureGroup();
    drawnRef.current = drawnItems;
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polyline: { shapeOptions: { color: '#10b981', weight: 4 } },
        polygon: { shapeOptions: { color: '#f59e0b', weight: 2, fillOpacity: 0.2 } },
        circle: false,
        circlemarker: false,
        rectangle: { shapeOptions: { color: '#f59e0b' } },
        marker: true,
      },
      edit: { featureGroup: drawnItems, remove: false, edit: false },
    });
    map.addControl(drawControl);

    const handler = (e) => {
      const layer = e.layer;
      const gj = layer.toGeoJSON();
      let geom = gj.geometry;
      // Convert rectangle (returned as Polygon) — already GeoJSON
      onCreate?.(geom);
      drawnItems.removeLayer(layer); // we re-render from server data
    };
    map.on(L.Draw.Event.CREATED, handler);

    return () => {
      map.off(L.Draw.Event.CREATED, handler);
      map.removeControl(drawControl);
      map.removeLayer(drawnItems);
      drawnRef.current = null;
    };
  }, [map, canEdit, onCreate]);

  return null;
}

// ==================== Item Detail Dialog ====================
function GeoEditDialog({ open, item, canEdit, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (open && item) {
      setForm({
        name: item.name || '',
        description: item.description || '',
      });
    }
  }, [open, item]);

  if (!form || !item) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="geo-edit-dialog">
        <DialogHeader>
          <DialogTitle>Geo element redaktəsi</DialogTitle>
          <DialogDescription className="sr-only">
            Geo elementin adı və açıqlamasının redaktəsi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Ad *</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              disabled={!canEdit}
              data-testid="geo-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Açıqlama</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              disabled={!canEdit}
              data-testid="geo-description-input"
            />
          </div>
          <div className="text-xs text-zinc-500 space-y-1 border-t pt-2">
            <div>
              <Badge variant="outline" className="text-[10px]">
                {item.geometry_type}
              </Badge>{' '}
              <span className="text-zinc-400">
                {item.source === 'manual' ? 'Manual yaradılıb' : `KMZ idxal (${item.source_file || ''})`}
              </span>
            </div>
            {item.properties && Object.keys(item.properties).length > 0 && (
              <div className="mt-2">
                <div className="font-semibold text-zinc-600 mb-1">Property-lər:</div>
                <div className="bg-zinc-50 rounded p-2 text-[11px] max-h-32 overflow-y-auto">
                  {Object.entries(item.properties).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <span className="text-zinc-500">{k}:</span>
                      <span className="text-zinc-800 truncate">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => onDelete(item)}
              className="text-red-600 hover:text-red-700 mr-auto"
              data-testid="geo-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Sil
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-1" /> Bağla
          </Button>
          {canEdit && (
            <Button
              onClick={() => onSave(form)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="geo-save-btn"
            >
              <Save className="h-4 w-4 mr-1" /> Yadda saxla
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Create Dialog (after drawing) ====================
function GeoCreateDialog({ open, pendingGeom, onConfirm, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="geo-create-dialog">
        <DialogHeader>
          <DialogTitle>Yeni geo element</DialogTitle>
          <DialogDescription className="sr-only">
            Çəkilmiş şəklin adı və açıqlamasının daxil edilməsi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {pendingGeom && (
            <Badge variant="outline" className="text-xs">
              {pendingGeom.type}
            </Badge>
          )}
          <div>
            <label className="text-sm font-medium">Ad *</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="Məsələn: Tower-3"
              data-testid="geo-create-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Açıqlama</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="geo-create-description-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Ləğv et
          </Button>
          <Button
            disabled={!name.trim()}
            onClick={() => onConfirm({ name: name.trim(), description })}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="geo-create-confirm-btn"
          >
            Yarat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== Polyline/Polygon overlay ====================
function ShapesLayer({ items, onItemClick }) {
  return (
    <>
      {items.map((it) => {
        const color = STATUS_TYPE_COLORS[it.geometry_type] || '#6366f1';
        if (it.geometry.type === 'LineString') {
          return (
            <Polyline
              key={it.id}
              positions={swapLine(it.geometry.coordinates)}
              pathOptions={{ color, weight: 4, opacity: 0.85 }}
              eventHandlers={{ click: () => onItemClick(it) }}
            >
              <Popup>
                <div className="text-sm font-semibold">{it.name}</div>
                {it.description && <div className="text-xs text-zinc-600">{it.description}</div>}
              </Popup>
            </Polyline>
          );
        }
        if (it.geometry.type === 'MultiLineString') {
          return it.geometry.coordinates.map((line, idx) => (
            <Polyline
              key={`${it.id}-${idx}`}
              positions={swapLine(line)}
              pathOptions={{ color, weight: 4, opacity: 0.85 }}
              eventHandlers={{ click: () => onItemClick(it) }}
            />
          ));
        }
        if (it.geometry.type === 'Polygon') {
          return (
            <Polygon
              key={it.id}
              positions={swapPolygon(it.geometry.coordinates)}
              pathOptions={{ color, weight: 2, fillColor: color, fillOpacity: 0.2 }}
              eventHandlers={{ click: () => onItemClick(it) }}
            >
              <Popup>
                <div className="text-sm font-semibold">{it.name}</div>
                {it.description && <div className="text-xs text-zinc-600">{it.description}</div>}
              </Popup>
            </Polygon>
          );
        }
        if (it.geometry.type === 'MultiPolygon') {
          return it.geometry.coordinates.map((poly, idx) => (
            <Polygon
              key={`${it.id}-${idx}`}
              positions={swapPolygon(poly)}
              pathOptions={{ color, weight: 2, fillColor: color, fillOpacity: 0.2 }}
              eventHandlers={{ click: () => onItemClick(it) }}
            />
          ));
        }
        return null;
      })}
    </>
  );
}

// ==================== Main ====================
export default function ProjectMapView({ project, canEdit, onChanged }) {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fitTrigger, setFitTrigger] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingGeom, setPendingGeom] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${project.id}/geo`, { headers });
      setItems(r.data || []);
      setFitTrigger((x) => x + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Geo məlumatlar yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, [project?.id, headers]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleDrawCreate = useCallback((geom) => {
    setPendingGeom(geom);
    setCreateOpen(true);
  }, []);

  const handleCreateConfirm = async ({ name, description }) => {
    if (!pendingGeom) return;
    try {
      await axios.post(
        `${API}/projects/${project.id}/geo`,
        { name, description, geometry: pendingGeom },
        { headers }
      );
      toast.success('Geo element yaradıldı');
      setCreateOpen(false);
      setPendingGeom(null);
      fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Yaradılmadı');
    }
  };

  const handleItemClick = useCallback((it) => {
    setEditingItem(it);
    setEditOpen(true);
  }, []);

  const handleSave = async (form) => {
    try {
      await axios.put(
        `${API}/projects/${project.id}/geo/${editingItem.id}`,
        { name: form.name, description: form.description },
        { headers }
      );
      toast.success('Yeniləndi');
      setEditOpen(false);
      setEditingItem(null);
      fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`"${it.name}" silinsin?`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/geo/${it.id}`, { headers });
      toast.success('Silindi');
      setEditOpen(false);
      setEditingItem(null);
      fetchItems();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  const pointItems = useMemo(
    () => items.filter((i) => i.geometry?.type === 'Point' || i.geometry?.type === 'MultiPoint'),
    [items]
  );
  const shapeItems = useMemo(
    () => items.filter((i) => !['Point', 'MultiPoint'].includes(i.geometry?.type)),
    [items]
  );

  // Default center: Baku, Azerbaijan
  const defaultCenter = [40.4093, 49.8671];

  return (
    <div className="relative" data-testid="project-map-view">
      <style>{`
        .custom-marker-cluster div {
          background: rgba(147, 51, 234, 0.85);
          color: white;
          border-radius: 50%;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          border: 3px solid rgba(147, 51, 234, 0.35);
          box-shadow: 0 1px 5px rgba(0,0,0,.2);
        }
        .cluster-small { font-size: 12px; }
        .cluster-medium {
          font-size: 13px; background: rgba(217, 70, 239, 0.9);
          border-color: rgba(217, 70, 239, 0.35);
        }
        .cluster-large {
          font-size: 14px; background: rgba(220, 38, 38, 0.92);
          border-color: rgba(220, 38, 38, 0.35);
        }
        .leaflet-container { font-family: inherit; }
      `}</style>

      <MapContainer
        center={defaultCenter}
        zoom={11}
        style={{ height: '520px', width: '100%', borderRadius: 8 }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ClusterLayer pointItems={pointItems} onItemClick={handleItemClick} />
        <ShapesLayer items={shapeItems} onItemClick={handleItemClick} />
        <FitBounds items={items} trigger={fitTrigger} />
        <DrawControl canEdit={canEdit} onCreate={handleDrawCreate} />
      </MapContainer>

      {loading && (
        <div className="absolute top-2 right-2 bg-white rounded-md shadow px-3 py-1 text-xs text-zinc-700 z-[1000]">
          Yüklənir...
        </div>
      )}

      {items.length === 0 && !loading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-white/95 rounded-lg px-4 py-3 shadow-lg text-sm text-zinc-600 pointer-events-auto">
            <div className="font-semibold mb-1">Xəritədə geo element yoxdur</div>
            <div className="text-xs text-zinc-500">
              {canEdit
                ? 'Soldakı çəkmə alətləri ilə yeni element yarada və ya KMZ idxal edə bilərsiniz.'
                : 'Bu layihə üçün hələ geo element yoxdur.'}
            </div>
          </div>
        </div>
      )}

      <GeoEditDialog
        open={editOpen}
        item={editingItem}
        canEdit={canEdit}
        onClose={() => {
          setEditOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      <GeoCreateDialog
        open={createOpen}
        pendingGeom={pendingGeom}
        onConfirm={handleCreateConfirm}
        onCancel={() => {
          setCreateOpen(false);
          setPendingGeom(null);
        }}
      />
    </div>
  );
}
